import { useEffect, useState } from 'react';

import { useAuthContext } from '@/contexts/auth-context';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';
import { isSourceFreemium } from '@/utils/is-source-freemium';

export const FREEMIUM_MIGRATION_BANNER_KEY_PREFIX =
  'source.campaigns.freemiumMigrationBanner';

const shownKeysThisPageLoad = new Set<string>();

type BannerState = { seenAt: number };

const UNSEEN: BannerState = { seenAt: 0 };

export function freemiumMigrationBannerStorageKey(
  externalId: string | undefined,
): string {
  return `${FREEMIUM_MIGRATION_BANNER_KEY_PREFIX}:${externalId ?? 'unknown'}`;
}

export function resetFreemiumMigrationBannerSession(): void {
  shownKeysThisPageLoad.clear();
}

export function useFreemiumMigrationBanner(): {
  show: boolean;
  dismiss: () => void;
} {
  const { whoami } = useAuthContext();
  const isFreemium = isSourceFreemium(whoami);
  const key = freemiumMigrationBannerStorageKey(whoami?.external_id);

  const [stored, setStored] = useLocalStorageState<BannerState>(
    isFreemium ? key : null,
    UNSEEN,
  );

  // Module-level set survives React StrictMode remount so the first view
  // stays visible in the same page load after seenAt is written.
  const [showOnThisMount] = useState(() => {
    if (!isFreemium) return false;
    if (shownKeysThisPageLoad.has(key)) return true;
    if (stored.seenAt > 0) return false;
    shownKeysThisPageLoad.add(key);
    return true;
  });

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!showOnThisMount) return;
    setStored({ seenAt: Date.now() });
  }, [setStored, showOnThisMount]);

  return {
    show: isFreemium && showOnThisMount && !dismissed,
    dismiss: () => setDismissed(true),
  };
}
